/**
 * Enhanced Spots Search Provider
 * 
 * Implements the search logic for the Spots tab context:
 * - Evidence-based matching (same files)
 * - Content similarity matching
 * - User scope filtering
 * - Flexible search criteria
 */

import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { findSimilarJobs } from '../../../../utils/jobSimilarityMatcher';
import BaseSearchProvider from './BaseSearchProvider';

export class EnhancedSpotsSearchProvider extends BaseSearchProvider {
  getDefaultCriteria() {
    return {
      searchScope: 'all_users',
      evidenceScope: 'all_evidence',
      selectedEvidenceId: '',
      matchType: 'similar_content'
    };
  }

  getSearchControls() {
    return [
      {
        id: 'searchScope',
        type: 'radio-group',
        label: 'Search Scope',
        options: [
          {
            value: 'all_users',
            label: 'All Users',
            description: 'Search jobs from all users in the system for complete investigation overview',
            helpText: 'COMPREHENSIVE: Search jobs from all users in the system.\n\nUSE FOR: Complete investigation overview, finding all related work.\n\nSCENARIO: See every job that analyzed your evidence or found similar content, regardless of who ran it.'
          },
          {
            value: 'other_users',
            label: 'Other Users Only',
            description: 'Only show jobs from other investigators to discover external investigations',
            helpText: 'EXTERNAL FOCUS: Only show jobs from other investigators, exclude your own work.\n\nUSE FOR: Discovering external investigations, finding collaboration opportunities.\n\nSCENARIO: See what other investigators found when analyzing your evidence or similar content.'
          },
          {
            value: 'my_jobs',
            label: 'My Jobs Only',
            description: 'Review your own jobs from other cases to find patterns in your work',
            helpText: 'PERSONAL REVIEW: Only show your own jobs from other cases.\n\nUSE FOR: Reviewing your past work, finding patterns in your investigations.\n\nSCENARIO: See your own jobs that analyzed similar evidence or found similar content in other cases.'
          }
        ]
      },
      {
        id: 'evidenceScope',
        type: 'radio-group',
        label: 'Evidence to Match',
        options: [
          {
            value: 'all_evidence',
            label: 'Any Evidence in Case',
            description: 'Find jobs related to any evidence file in this casefile for comprehensive review',
            helpText: 'BROAD SEARCH: Find jobs related to any evidence file in this casefile.\n\nUSE FOR: Comprehensive case review, finding all related work.\n\nSCENARIO: Discover jobs that analyzed any video, image, or document from this case.'
          },
          {
            value: 'specific_evidence',
            label: 'Specific Evidence File',
            description: 'Focus on jobs related to one specific evidence file for detailed investigation',
            helpText: 'TARGETED SEARCH: Focus on jobs related to one specific evidence file.\n\nUSE FOR: Detailed investigation of specific evidence, tracking file usage.\n\nSCENARIO: See all jobs that analyzed your specific surveillance video or document.'
          }
        ]
      },
      {
        id: 'matchType',
        type: 'radio-group',
        label: 'Match Type',
        options: [
          {
            value: 'both',
            label: 'Same Evidence + Similar Content',
            description: 'Most comprehensive search combining file sharing and content similarity',
            helpText: 'COMPREHENSIVE SEARCH: Finds jobs that analyzed your exact files AND jobs with similar findings (vehicles, plates, people).\n\nUSE FOR: Most thorough investigation review, getting complete picture of related work.\n\nSCENARIO: Shows both jobs that analyzed your exact traffic camera video AND jobs that found the same license plate in different videos from other locations.'
          },
          {
            value: 'same_evidence',
            label: 'Same Evidence Files Only',
            description: 'Find jobs that analyzed your identical evidence files for data sharing investigation',
            helpText: 'DIRECT FILE SHARING: Only finds jobs that analyzed your identical evidence files (same video, image, document).\n\nUSE FOR: Investigating data sharing, finding collaboration, fastest/most reliable results.\n\nSCENARIO: If you have \'surveillance_cam_001.mp4\', this shows all other investigators who analyzed that exact same file.'
          },
          {
            value: 'similar_content',
            label: 'Similar Content Only',
            description: 'Find jobs with similar analysis results regardless of evidence files used',
            helpText: 'CONTENT PATTERN MATCHING: Finds jobs with similar analysis results (same vehicles, plates, people) regardless of evidence files.\n\nUSE FOR: Finding related investigations based on content patterns, tracking subjects across cases.\n\nSCENARIO: Jobs that detected the same license plate \'ABC123\' or same person even in completely different videos from different cases.'
          }
        ]
      }
    ];
  }

  async search(criteria, data) {
    const { casefileId, currentUserId, jobs = [], evidenceOptions = [] } = data;
    const { searchScope, evidenceScope, selectedEvidenceId, matchType } = criteria;

    console.log('🔍 Enhanced Spots Search starting with:', { criteria, dataKeys: Object.keys(data) });

    try {
      // Step 1: Get evidence IDs to search for
      let targetEvidenceIds = [];
      if (evidenceScope === 'specific_evidence' && selectedEvidenceId) {
        targetEvidenceIds = [selectedEvidenceId];
      } else {
        // All evidence in this casefile
        targetEvidenceIds = evidenceOptions.map(ev => ev.id);
      }

      if (targetEvidenceIds.length === 0) {
        console.log('❌ No evidence IDs to search for');
        return [];
      }

      console.log(`🎯 Searching for ${targetEvidenceIds.length} evidence IDs`);

      // Step 2: Find jobs that match evidence (same evidence files)
      let evidenceMatches = [];
      if (matchType === 'same_evidence' || matchType === 'both') {
        evidenceMatches = await this.findEvidenceMatches(
          targetEvidenceIds,
          evidenceOptions,
          searchScope,
          currentUserId,
          casefileId
        );
        console.log(`📁 Found ${evidenceMatches.length} evidence matches`);
      }

      // Step 3: Find jobs with similar content
      let contentMatches = [];
      if (matchType === 'similar_content' || matchType === 'both') {
        contentMatches = await this.findContentMatches(
          jobs,
          searchScope,
          currentUserId,
          casefileId,
          evidenceOptions
        );
        console.log(`🔍 Found ${contentMatches.length} content matches`);
      }

      // Step 4: Combine and deduplicate results
      const allMatches = [...evidenceMatches, ...contentMatches];
      const uniqueMatches = new Map();

      allMatches.forEach(match => {
        const key = match.jobId || match.id;
        const existing = uniqueMatches.get(key);

        if (!existing) {
          uniqueMatches.set(key, match);
        } else {
          // Prefer evidence matches over content matches
          if (match.matchType === 'evidence' && existing.matchType === 'content') {
            uniqueMatches.set(key, match);
          }
        }
      });

      const results = Array.from(uniqueMatches.values());
      console.log(`✅ Final results: ${evidenceMatches.length} evidence + ${contentMatches.length} content = ${results.length} unique`);

      return results.slice(0, 50); // Limit results

    } catch (error) {
      console.error('❌ Enhanced Spots Search failed:', error);
      return [];
    }
  }

  async findEvidenceMatches(targetEvidenceIds, evidenceOptions, searchScope, currentUserId, casefileId) {
    const matches = [];

    for (const evidenceId of targetEvidenceIds) {
      const q = query(
        collection(db, 'jobs'),
        where('evidenceId', '==', evidenceId),
        where('status', 'in', ['completed', 'success'])
      );

      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        const jobData = { id: doc.id, ...doc.data() };

        // Apply user scope filtering
        const shouldInclude =
          (searchScope === 'all_users') ||
          (searchScope === 'other_users' && jobData.userId !== currentUserId) ||
          (searchScope === 'my_jobs' && jobData.userId === currentUserId);

        // Exclude jobs from current casefile
        if (shouldInclude && jobData.casefileId !== casefileId) {
          const evidenceFile = evidenceOptions.find(ev => ev.id === evidenceId);
          matches.push({
            ...jobData,
            jobId: jobData.id,
            matchReason: 'Same Evidence File',
            evidenceFileName: evidenceFile?.fileName || 'Unknown',
            matchType: 'evidence',
            evidenceFiles: evidenceFile ? [{
              // Pass through ALL evidence properties including downloadURL
              ...evidenceFile,
              // Override casefileId with the job's casefileId (where it was actually used)
              casefileId: jobData.casefileId
            }] : []
          });
        }
      });
    }

    return matches;
  }

  async findContentMatches(jobs, searchScope, currentUserId, casefileId, evidenceOptions) {
    const matches = [];
    const completedJobs = jobs.filter(j => j.status === 'completed');

    console.log(`🔍 Content matching: ${completedJobs.length} completed jobs available`);

    if (completedJobs.length === 0) {
      console.log('❌ No completed jobs available for content similarity matching');
      return matches;
    }

    for (const job of completedJobs.slice(0, 3)) { // Limit to prevent too many API calls
      try {
        console.log(`🔍 Attempting content similarity search for job ${job.id}`);

        // First check if the job has results and load them
        const jobResultsQuery = query(
          collection(db, 'analysisResults'),
          where('jobId', '==', job.id)
        );
        const resultsSnapshot = await getDocs(jobResultsQuery);

        if (resultsSnapshot.empty) {
          console.log(`❌ Job ${job.id} has no analysis results, skipping content similarity`);
          continue;
        }

        // Load the actual results data
        const resultsData = [];
        resultsSnapshot.forEach(doc => {
          resultsData.push({ id: doc.id, ...doc.data() });
        });

        console.log(`📊 Job ${job.id} has ${resultsData.length} analysis results`);

        // Find the first result with structured data
        const validResult = resultsData.find(result =>
          result.structuredData && typeof result.structuredData === 'object'
        );

        if (!validResult) {
          console.log(`❌ Job ${job.id} results lack structured data, skipping content similarity`);
          continue;
        }

        console.log(`✅ Using result ${validResult.id} for similarity matching`);

        const similarJobs = await findSimilarJobs(job.id, validResult, {
          maxResults: 10,
          minSimilarityScore: 0.3,
          searchRadius: 'all',
          includeCurrentCasefile: false,
          onlyOtherUsers: searchScope === 'other_users'
        });

        console.log(`📊 Found ${similarJobs.length} similar jobs for ${job.id}`);

        for (const similar of similarJobs) {
          // Apply user scope filtering
          const shouldInclude =
            (searchScope === 'all_users') ||
            (searchScope === 'other_users' && similar.userId !== currentUserId) ||
            (searchScope === 'my_jobs' && similar.userId === currentUserId);

          if (shouldInclude) {
            // Try to find evidence file information for this job
            let evidenceFiles = [];
            if (similar.evidenceId) {
              // Look for evidence in the current casefile's evidence options first
              const evidenceFile = evidenceOptions.find(ev => ev.id === similar.evidenceId);
              if (evidenceFile) {
                evidenceFiles = [{
                  ...evidenceFile,
                  // Override casefileId with the job's casefileId (where it was actually used)
                  casefileId: similar.casefileId
                }];
              } else if (similar.casefileId) {
                // Attempt to fetch the evidence document from the other casefile to retrieve downloadURL/contentType
                try {
                  const evRef = doc(db, 'casefiles', similar.casefileId, 'evidence', similar.evidenceId);
                  const evSnap = await getDoc(evRef);
                  if (evSnap.exists()) {
                    const evData = evSnap.data();
                    evidenceFiles = [{
                      id: similar.evidenceId,
                      fileName: evData.fileName || evData.name || `Evidence File (${similar.evidenceId.substring(0, 8)}...)`,
                      name: evData.fileName || evData.name,
                      downloadURL: evData.downloadURL || null,
                      thumbnailURL: evData.thumbnailURL || null,
                      fileSize: evData.fileSize || evData.size || null,
                      contentType: evData.contentType || null,
                      createdAt: evData.createdAt || evData.uploadDate || null,
                      isPublic: evData.isPublic || false,
                      casefileId: similar.casefileId
                    }];
                  }
                } catch (e) {
                  // Fall through to placeholder below
                }
              }

              // If still empty, create a placeholder
              if (evidenceFiles.length === 0) {
                evidenceFiles = [{
                  id: similar.evidenceId,
                  fileName: `Evidence File (${similar.evidenceId.substring(0, 8)}...)`,
                  name: `Evidence File (${similar.evidenceId.substring(0, 8)}...)`,
                  downloadURL: null,
                  size: null,
                  uploadDate: null,
                  duration: null,
                  resolution: null,
                  type: 'Unknown',
                  casefileId: similar.casefileId
                }];
              }
            }

            matches.push({
              ...similar,
              matchReason: `Similar Content (${Math.round(similar.similarityScore * 100)}%)`,
              matchType: 'content',
              evidenceFiles
            });
          }
        }
      } catch (error) {
        console.warn(`⚠️ Failed to find similar jobs for ${job.id}:`, error.message);
        // Continue with other jobs even if one fails
      }
    }

    return matches;
  }

  getAvailableFilters() {
    return [
      {
        id: 'matchType',
        label: 'Match Type',
        options: [
          { value: 'evidence', label: 'Evidence Matches' },
          { value: 'content', label: 'Content Matches' }
        ]
      },
      {
        id: 'confidence',
        label: 'Confidence',
        options: [
          { value: 'high', label: 'High Confidence (70%+)' },
          { value: 'medium', label: 'Medium Confidence (40-70%)' },
          { value: 'low', label: 'Low Confidence (<40%)' }
        ]
      },
      {
        id: 'provider',
        label: 'Provider',
        options: [
          { value: 'vertex_ai_vision', label: 'Google Vertex AI' },
          { value: 'aws_bedrock', label: 'AWS Bedrock' },
          { value: 'openai', label: 'OpenAI' }
        ]
      }
    ];
  }

  applyFilter(results, filterType, filterValue) {
    switch (filterType) {
      case 'matchType':
        return results.filter(r => r.matchType === filterValue);

      case 'confidence':
        return results.filter(r => {
          const score = r.similarityScore || 0;
          switch (filterValue) {
            case 'high': return score >= 0.7;
            case 'medium': return score >= 0.4 && score < 0.7;
            case 'low': return score < 0.4;
            default: return true;
          }
        });

      case 'provider':
        return results.filter(r => r.provider === filterValue);

      default:
        return results;
    }
  }

  getStatistics(results) {
    const stats = super.getStatistics(results);
    
    // Add provider breakdown
    const providers = {};
    results.forEach(r => {
      const provider = r.provider || 'Unknown';
      providers[provider] = (providers[provider] || 0) + 1;
    });

    // Add most common provider
    const topProvider = Object.entries(providers)
      .sort(([,a], [,b]) => b - a)[0];
    
    if (topProvider) {
      stats['Top Provider'] = `${topProvider[0]} (${topProvider[1]})`;
    }

    return stats;
  }

  validateCriteria(criteria, data) {
    const errors = [];

    if (criteria.evidenceScope === 'specific_evidence' && !criteria.selectedEvidenceId) {
      errors.push('Please select a specific evidence file when using "Specific Evidence File" option');
    }

    if (!data.evidenceOptions || data.evidenceOptions.length === 0) {
      errors.push('No evidence files found in this casefile');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default EnhancedSpotsSearchProvider;
